import { useState, useEffect } from "react";
import EscMonthlyReportFilter from "./EscMonthlyReportFilter";
import SpinnerController from "../spinnerController/Spinner";
import Pagination from "./Pagination";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const getNameFromEmail = (email) => {
  if (!email) return "";
  const localPart = email.split("@")[0];
  const nameParts = localPart.split(".");
  if (nameParts.length >= 2) {
    return `${nameParts[0]} ${nameParts[1]}`.replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
  }
  return localPart.replace(/\b\w/g, (char) => char.toUpperCase());
};

const EscMonthlyReportTable = ({
  month,
  handleFilter,
  ticketDetails,
  setProject,
  filterError,
  years,
  setYear,
  setMonth,
}) => {
  const [userData, setUserData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "totalCount",
    direction: "asc",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosPrivate.get("/users");
        const userData = response.data.map((user) => ({
          email: user.email,
          title: user.title,
          username: user.username,
        })); // Extracting email and title
        setUserData(userData);
        console.log(userData); // Logging email and title
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [axiosPrivate]);

  const countEmailOccurrences = (tickets) => {
    const emailCount = {};
    tickets.forEach((ticket) => {
      if (ticket.email) {
        emailCount[ticket.email] = (emailCount[ticket.email] || 0) + 1;
      }
    });
    return emailCount;
  };

  const emailOccurrences = ticketDetails.tickets
    ? countEmailOccurrences(ticketDetails.tickets)
    : {};

  const getUniqueEmails = (tickets) => {
    const seenEmails = new Set();
    return tickets
      .filter((ticket) => {
        if (ticket.email && !seenEmails.has(ticket.email)) {
          seenEmails.add(ticket.email);
          return true;
        }
        return false;
      })
      .map((ticket) => ({ ...ticket, count: emailOccurrences[ticket.email] }));
  };

  const uniqueTickets = ticketDetails.tickets
    ? getUniqueEmails(ticketDetails.tickets)
    : [];

  const sortedUniqueTickets = uniqueTickets.sort((a, b) => {
    let comparison = 0;
    if (sortConfig.key === "reporterName") {
      comparison = a.reporterName.localeCompare(b.reporterName);
    } else if (sortConfig.key === "email") {
      comparison = a.email.localeCompare(b.email);
    } else if (sortConfig.key === "title") {
      comparison = (
        userData.find((user) => user.email === a.email)?.title || ""
      ).localeCompare(
        userData.find((user) => user.email === b.email)?.title || ""
      );
    } else if (sortConfig.key === "totalCount") {
      comparison = a.count - b.count;
    }
    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "▲";
  };
  const filteredTickets = uniqueTickets.filter(
    (ticket) =>
      ticket.assigneeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div
        className="max-w-6xl flex flex-col px-10 justify-center container mx-auto pt-10"
        style={{ maxWidth: "1400px" }}
      >
        <div className="w-full text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-700">
            Monthly Escalation Report
          </h1>
        </div>

        {/* <div className="mb-4 flex justify-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                aria-hidden="true"
                className="w-5 h-5 text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </div>
            <input
              type="text"
              className="w-64 p-2 border border-gray-300 rounded text-center pl-10"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div> */}

        <div className="table-wrp block max-h-[41rem] overflow-x-auto relative shadow-md sm:rounded-lg bg-gray-500 w-full">
          <table className="w-full text-sm text-center text-gray-500">
            <caption className="sticky top-0 p-5 text-lg font-semibold text-left text-gray-700 bg-white border-b-2 border-gray-500">
              <div className="flex items-center justify-center aligncenter justify-between">
                <EscMonthlyReportFilter
                  month={month}
                  handleFilter={handleFilter}
                  setMonth={setMonth}
                  setYear={setYear}
                  years={years}
                />
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg
                        aria-hidden="true"
                        className="w-5 h-5 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="w-64 p-0.5 border border-gray-300 rounded text-center pl-10 "
                      placeholder="Search by name or email"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
              </div>
              <div>
                {filterError && (
                  <h6 className="pt-2 text-xs font-sm text-red-700">
                    * {filterError}
                  </h6>
                )}
              </div>
            </caption>
            <thead className="sticky top-[88px] text-xs text-gray-900 uppercase bg-white border-b-2 border-gray-500">
              <tr>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap"
                  onClick={() => requestSort("reporterName")}
                  style={{ cursor: "pointer" }}
                >
                  Reporter Name
                  {/* {getSortArrow('reporterName')} */}
                </th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap"
                  onClick={() => requestSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  Reporter Email {getSortArrow("email")}
                </th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap"
                  onClick={() => requestSort("title")}
                  style={{ cursor: "pointer" }}
                >
                  Title {getSortArrow("title")}
                </th>
                <th
                  scope="col"
                  className="py-3 px-6 no-wrap"
                  onClick={() => requestSort("totalCount")}
                  style={{ cursor: "pointer" }}
                >
                  Total Count {getSortArrow("totalCount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {!ticketDetails.tickets ? (
                <tr key="loading">
                  <td colSpan="4" className="bg-gray-50 p-4">
                    <div className="flex justify-center">
                      <SpinnerController />
                    </div>
                  </td>
                </tr>
              ) : !ticketDetails.tickets.length ? (
                <tr key="nodata">
                  <td className="bg-gray-50 p-4" colSpan="4">
                    No data available
                  </td>
                </tr>
              ) : (
                <>
                  {filteredTickets.map((ticket, i) => {
                    const user = userData.find(
                      (user) => user.email === ticket.email
                    );
                    return (
                      <tr
                        key={i}
                        className="bg-white text-gray-900 border-b hover:bg-myellow"
                      >
                        <td className="py-4 px-6 bg-myellow font-medium font-semibold text-gray-900 no-wrap">
                          {getNameFromEmail(ticket.email)}
                        </td>
                        <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                          {ticket.email}
                        </td>
                        <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                          {user ? user.title : ticket.role || ""}
                        </td>
                        <td className="py-4 px-6 font-medium font-semibold text-gray-900 no-wrap">
                          {ticket.count}
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        month={month}
        pageNo={uniqueTickets.length}
        length={ticketDetails.tLength}
      />
    </>
  );
};

export default EscMonthlyReportTable;
